import { PlusIcon, SettingsIcon, TrashIcon } from "@xola/icons";
import { Alert, FormGroup, Label, PopoverList, Spinner, Table, Tooltip } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import { isEquipmentBased, SwitchField } from "common";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    EquipmentAllocationStrategy,
    EquipmentCatalogItem,
    Experience,
    Permission,
    PrioritizedResourceID,
    Resource,
    ResourceType,
    UnitType,
} from "types";
import { api } from "../../../api/api";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { ExperienceDurationField } from "../ExperienceDurationField";
import { ExperienceResourceSettingValues } from "./ExperienceResourceTypes";
import { OverrideAlertBox } from "@/components/Common/OverrideAlertBox";
import { EquipmentAllocationOptions } from "@/components/Resources/EquipmentManagement/GlobalSettings";
import { useSeller } from "@/hooks/useAccount";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";
import { isMultiCheckInAllowed } from "@/models/Experience";

interface ExperienceEquipmentFieldsProps {
    experience: Experience;
    setCustomOrderChanged: (value: boolean) => void;
}

const sectionClassNames = { content: "lg:!max-w-full" };

export const ExperienceEquipmentFields = ({ experience, setCustomOrderChanged }: ExperienceEquipmentFieldsProps) => {
    const seller = useSeller();
    const { values, setFieldValue, errors } = useFormikContext<ExperienceResourceSettingValues>();
    const showTrackEquipment = !isMultiCheckInAllowed(seller, experience);
    const isEarlyReturnEnabled = showTrackEquipment && (!isEquipmentBased(experience) || values.allowCheckoutAtArrival);
    const tooltipContent = !showTrackEquipment
        ? "Early returns cannot be enabled as this experience has multiple check-ins configured"
        : "Enable equipment check out at arrival to track returns";

    if (!values) {
        return <Loader />;
    }

    return (
        <ContentLayout.Section classNames={sectionClassNames} title="Equipment">
            <div className="space-y-4">
                {isEquipmentBased(experience) ? (
                    <EquipmentInfoBanner experience={experience} />
                ) : (
                    <ExperienceResourceAssignment experience={experience} />
                )}

                <IsGranted permissions={Permission.MODIFY_RESOURCES_EQUIPMENT}>
                    {/* This field is readonly so no change handler */}
                    <ExperienceDurationField
                        isDisabled
                        name="eventDuration"
                        label="Event Duration"
                        originalValue={experience?.eventDuration ?? 0}
                        value={values.eventDuration ?? 0}
                        onChange={() => null}
                    />
                    <ExperienceDurationField
                        name="duration"
                        label="Inventory Duration"
                        tooltip="The amount of time an event booked on this product will occupy assigned equipment items"
                        originalValue={experience?.duration ?? 0}
                        value={values.duration}
                        error={errors?.duration}
                        onChange={(value: number) => setFieldValue("duration", value)}
                    />

                    <FormGroup className="space-y-1">
                        <Label>Track Equipment</Label>
                        {isEquipmentBased(experience) && (
                            <div>
                                <SwitchField
                                    size="medium"
                                    isChecked={values.allowCheckoutAtArrival ?? false}
                                    data-testid="experience-equipment-fields-allow-checkout-tab"
                                    onChange={(value: boolean) => setFieldValue("allowCheckoutAtArrival", value)}
                                >
                                    <span>Allow equipment to be checked out at arrival</span>
                                </SwitchField>
                            </div>
                        )}
                        <div>
                            <Tooltip placement="right" content={tooltipContent} disabled={isEarlyReturnEnabled}>
                                <SwitchField
                                    size="medium"
                                    isChecked={values.earlyReturn ?? false}
                                    isDisabled={!isEarlyReturnEnabled}
                                    data-testid="experience-equipment-fields-resource-early-returns-tab"
                                    onChange={(value: boolean) => setFieldValue("earlyReturn", value)}
                                >
                                    <span>Track returns for checked out equipment</span>
                                </SwitchField>
                            </Tooltip>
                        </div>
                    </FormGroup>
                </IsGranted>

                <EquipmentAssignmentRuleSettings
                    experience={experience}
                    setCustomOrderChanged={setCustomOrderChanged}
                />
            </div>
        </ContentLayout.Section>
    );
};

interface ExperienceResourceAssignmentProps {
    experience: Experience;
}

const ExperienceResourceAssignment = ({ experience }: ExperienceResourceAssignmentProps) => {
    const invalidate = useInvalidate();
    const [visible, { show, hide }] = useVisibility(false);
    const { data: allResources } = useInfiniteCollection<Resource>("resources");

    const assignedResourceIds = new Set((experience.resources ?? []).map((r) => r.resource.id));
    const assignedResources = (allResources ?? []).filter(
        (resource) => resource.type === ResourceType.EQUIPMENT && assignedResourceIds.has(resource.id),
    );
    const unassignedResources = (allResources ?? []).filter(
        (resource) => resource.type === ResourceType.EQUIPMENT && !assignedResourceIds.has(resource.id),
    );

    const { mutateAsync: addResource, isLoading: isAdding } = useMutation(async (resource: Resource) => {
        await api.post(`/experiences/${experience.id}/resources`, { resource: { id: resource.id }, priority: 0 });
        await invalidate("experiences", experience.id);
    });

    const {
        mutateAsync: removeResource,
        isLoading: isRemoving,
        variables: removingResourceId,
    } = useMutation(async (resourceId: ID) => {
        await api.delete(`/experiences/${experience.id}/resources/${resourceId}`);
        await invalidate("experiences", experience.id);
    });

    const isMutating = isAdding || isRemoving;

    const handleAddResource = useMemoizedFn(async (resource: Resource) => {
        hide();
        await addResource(resource);
    });

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <Label className="mb-0">Equipment</Label>
                    {isAdding && <Spinner size="tiny" />}
                </div>
                <PopoverList visible={visible} className="max-h-96 w-75 overflow-y-scroll" onClickOutside={hide}>
                    <SecondaryButton
                        disabled={unassignedResources.length === 0 || isMutating}
                        size="small"
                        icon={<PlusIcon />}
                        onClick={visible ? hide : show}
                    >
                        Add Equipment
                    </SecondaryButton>
                    {unassignedResources.map((resource) => (
                        <PopoverList.Item
                            key={resource.id}
                            name={resource.name}
                            className="bg-white"
                            onClickItem={async () => handleAddResource(resource)}
                        >
                            <NoTranslate>{resource.name}</NoTranslate>
                        </PopoverList.Item>
                    ))}
                </PopoverList>
            </div>
            {assignedResources.length > 0 && (
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header className="!text-sm">Equipment</Table.Header>
                            <Table.Header className="w-16 text-right !text-sm">Actions</Table.Header>
                        </Table.Row>
                    </Table.Head>
                    <Table.Body isStriped={false}>
                        {assignedResources.map((resource) => (
                            <Table.Row key={resource.id}>
                                <Table.Cell>{resource.name}</Table.Cell>
                                <Table.Cell className="w-16 text-right">
                                    <button
                                        type="button"
                                        className="text-gray-400 hover:text-red-500"
                                        disabled={isMutating}
                                        onClick={async () => {
                                            removeResource(resource.id);
                                        }}
                                    >
                                        {removingResourceId === resource.id ? <Spinner size="tiny" /> : <TrashIcon />}
                                    </button>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
};

interface EquipmentInfoBannerProps {
    experience: Experience;
}

const EquipmentInfoBanner = ({ experience }: EquipmentInfoBannerProps) => {
    const history = useHistory();
    const handleGoToPricing = () => history.push(`/products/experiences/${experience.id}/pricing`);
    const equipmentItems = (experience.catalog?.items ?? []).filter(
        (item) => item.unitType === UnitType.EQUIPMENT,
    ) as EquipmentCatalogItem[];

    return (
        <div className="space-y-4">
            <Label>Equipment</Label>

            <Alert color="primary">
                <span className="flex items-center space-x-2">
                    <span>Equipment for this experience must be managed from the Pricing section.</span>
                    <SecondaryButton size="tiny" onClick={handleGoToPricing}>
                        <SettingsIcon />
                    </SecondaryButton>
                </span>
            </Alert>

            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header className="!text-sm">Equipment</Table.Header>
                        <Table.Header className="!text-sm">Display Name</Table.Header>
                        <Table.Header className="!text-sm">Caption</Table.Header>
                    </Table.Row>
                </Table.Head>
                <Table.Body isStriped={false}>
                    {equipmentItems.map((item) => (
                        <Table.Row key={item.template?.id ?? item.id}>
                            <Table.Cell>{item.name}</Table.Cell>
                            <Table.Cell>{item.displayName ?? item.name}</Table.Cell>
                            <Table.Cell>{item.caption}</Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
};

export const EquipmentAssignmentRuleSettings = ({
    experience,
    setCustomOrderChanged,
}: ExperienceEquipmentFieldsProps) => {
    const { values, setFieldValue } = useFormikContext<ExperienceResourceSettingValues>();
    const [shouldOverride, setShouldOverride] = useState(!!values?.inventory);
    const history = useHistory();

    const {
        preferences: { inventory },
    } = useSeller();

    const hasInventory = !!values?.inventory;
    useEffect(() => {
        setShouldOverride(hasInventory);
    }, [hasInventory]);

    const handleOverrideSwitchChange = async (isOverride: boolean) => {
        // set global setting if no experience level is set
        if (isOverride && !values.inventory) {
            setFieldValue("inventory", {
                allocationStrategy: inventory.allocationStrategy,
                prioritizedResources: filterPrioritizedResource(experience, inventory.prioritizedResources),
            });
        }

        // unset inventory if setting is changed from Override to disable override
        if (!isOverride && values.inventory) {
            setFieldValue("inventory", undefined);
        }

        setShouldOverride(isOverride);
    };

    const handleGlobalAssignmentSettingLink = () => {
        history.push("/resources/equipment/settings");
    };

    return (
        <div className="w-150 pb-20">
            <Label>Automatic Equipment Assignment Rules</Label>
            {isEquipmentBased(experience) ? (
                <Alert color="primary">
                    Equipment selection is made by the guest at the time of purchase and bypasses automatic assignment
                    rules.
                </Alert>
            ) : (
                <>
                    <OverrideAlertBox
                        shouldOverride={shouldOverride}
                        switchText="Override global  automatic equipment assignment rules"
                        onChange={async (value) => {
                            handleOverrideSwitchChange(value);
                            setFieldValue("isFormDirty", true);
                        }}
                    >
                        <span>
                            This experience is using the{" "}
                            <a
                                className="cursor-pointer font-bold text-primary-dark"
                                onClick={handleGlobalAssignmentSettingLink}
                            >
                                Global Automatic Equipment Assignment Rules.
                            </a>
                            You can configure custom automatic equipment assignment rules specific to this experience by
                            overriding the global settings below.
                        </span>
                    </OverrideAlertBox>
                    {shouldOverride && (
                        <EquipmentAssignmentSettings
                            experience={experience}
                            setCustomOrderChanged={setCustomOrderChanged}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export const EquipmentAssignmentSettings = ({ experience, setCustomOrderChanged }: ExperienceEquipmentFieldsProps) => {
    const { values, setFieldValue } = useFormikContext<ExperienceResourceSettingValues>();

    const setAllocationStrategy = (strategy: EquipmentAllocationStrategy) => {
        setFieldValue("inventory.allocationStrategy", strategy);
    };

    const setPrioritizedResources = (value: PrioritizedResourceID[]) => {
        setFieldValue("inventory.prioritizedResources", value);
    };

    return (
        <div className="pt-5">
            <EquipmentAllocationOptions
                experience={experience}
                prioritizedResources={values.inventory?.prioritizedResources}
                allocationStrategy={values.inventory?.allocationStrategy ?? EquipmentAllocationStrategy.LARGEST_FIRST}
                setPrioritizedResources={setPrioritizedResources}
                setCustomOrderChanged={setCustomOrderChanged}
                onStrategyChange={setAllocationStrategy}
            />
        </div>
    );
};

const filterPrioritizedResource = (experience: Experience, resources?: PrioritizedResourceID[]) => {
    const resourceIdsFromExperience = new Set((experience?.resources || []).map((resource) => resource.resource.id));
    return resources?.filter(({ resource }) => resourceIdsFromExperience.has(resource.id));
};
